import { Schema as S } from "effect";

const DateText = S.String.pipe(S.check(S.isPattern(/^\d{4}-\d{2}-\d{2}$/)));
const LocalTimeText = S.String.pipe(S.check(S.isPattern(/^([01]\d|2[0-3]):[0-5]\d$/)));
/** Central calendar restriction intersects live inSuite type associations.
 * An explicit empty subset means no eligible calendar.
 * @since 1.1.8
 * @example const scope: BookingCalendarScope = { mode: "explicit", calendarIds: [] };
 */
export const BookingCalendarScope = S.Union(
  [S.Struct({ mode: S.Literal("all-linked") }),
  S.Struct({ mode: S.Literal("explicit"), calendarIds: S.Array(S.String.pipe(S.check(S.isMinLength(1)))) })],
);
/** Typed central calendar scope.
 * @since 1.1.8
 * @example const scope: BookingCalendarScope = { mode: "all-linked" };
 */
export type BookingCalendarScope = S.Schema.Type<typeof BookingCalendarScope>;
/** Inclusive local calendar-date horizon; closed days never extend it.
 * @since 1.1.8
 * @example const window: BookingDateWindow = { mode: "rolling", dates: 7 };
 */
export const BookingDateWindow = S.Union(
  [S.Struct({ mode: S.Literal("unrestricted") }),
  S.Struct({ mode: S.Literal("today") }),
  S.Struct({ mode: S.Literal("rolling"), dates: S.Int.pipe(S.check(S.isGreaterThan(0))) }),
  S.Struct({ mode: S.Literal("fixed"), startDate: DateText, endDate: DateText })],
);
/** Typed inclusive date horizon.
 * @since 1.1.8
 * @example const window: BookingDateWindow = { mode: "today" };
 */
export type BookingDateWindow = S.Schema.Type<typeof BookingDateWindow>;
/** Timed release is an additional intersection with the date horizon.
 * @since 1.1.8
 * @example const release: BookingRelease = { mode: "same-day", time: "09:00" };
 */
export const BookingRelease = S.Union(
  [S.Struct({ mode: S.Literal("none") }),
  S.Struct({ mode: S.Literals(["same-day", "previous-service-opening-day"]), time: LocalTimeText })],
);
/** Typed local release rule.
 * @since 1.1.8
 * @example const release: BookingRelease = { mode: "none" };
 */
export type BookingRelease = S.Schema.Type<typeof BookingRelease>;
/** Staff-owned controls with domain validation of real dates and compatible settings.
 * @since 1.1.8
 * @example const controls: BookingControls = { calendarScope: { mode: "all-linked" }, window: { mode: "unrestricted" }, release: { mode: "none" } };
 */
export const BookingControls = S.Struct({ calendarScope: BookingCalendarScope, window: BookingDateWindow, release: BookingRelease });
/** Typed appointment-type controls.
 * @since 1.1.8
 * @example const controls: BookingControls = { calendarScope: { mode: "all-linked" }, window: { mode: "today" }, release: { mode: "none" } };
 */
export type BookingControls = S.Schema.Type<typeof BookingControls>;
